import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { surgeryService, type SurgicalProcedure, type InformedConsent } from "../surgeryService";
import { useClinicSettings } from "../../../hooks/useClinicSettings";
import { parseApiDate } from '../../../lib/format';

const calcAge = (dob?: string) => {
  if (!dob) return null;
  return Math.floor((Date.now() - (parseApiDate(dob) ?? new Date(NaN)).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
};

const fmt = (d?: string) =>
  d ? (parseApiDate(d) ?? new Date(NaN)).toLocaleDateString("es-DO", { day: "2-digit", month: "long", year: "numeric" }) : "-";

const ITEM_TYPE_LABELS: Record<string, string> = {
  risk: "Riesgo",
  alternative: "Alternativa",
  consequence: "Consecuencia",
  note: "Nota",
};

const InformedConsentPrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [proc, setProc] = useState<SurgicalProcedure | null>(null);
  const [consent, setConsent] = useState<InformedConsent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { clinicName, clinicAddress, logoUrl } = useClinicSettings(proc?.doctor_id);

  useEffect(() => {
    const numericId = Number(id);
    Promise.all([
      surgeryService.getById(numericId),
      surgeryService.generateInformedConsent(numericId),
    ])
      .then(([procedureData, consentData]) => {
        setProc(procedureData);
        setConsent(consentData);
      })
      .catch(() => setError("Error generando el consentimiento informado."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!proc || !consent) return;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, [proc, consent]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "sans-serif" }}>
        <span>Generando consentimiento informado...</span>
      </div>
    );
  }

  if (error) {
    return <div style={{ padding: 40, color: "red" }}>{error}</div>;
  }

  if (!proc) {
    return <div style={{ padding: 40, color: "red" }}>Procedimiento no encontrado.</div>;
  }

  const doctor = proc.doctor;
  const patient = proc.patient;
  const age = (patient as any)?.birth_date ? calcAge((patient as any).birth_date) : null;

  const risks = consent?.items?.filter((i) => i.type === "risk") ?? [];
  const alternatives = consent?.items?.filter((i) => i.type === "alternative") ?? [];
  const notes = consent?.items?.filter((i) => i.type === "note" || i.type === "consequence") ?? [];

  return (
    <>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Times New Roman', Times, serif; font-size: 10.5pt; color: #111; background: white; }
        .page { width: 100%; max-width: 190mm; margin: 0 auto; padding: 12mm 16mm; min-height: 277mm; }
        .header { border-bottom: 3px double #111; padding-bottom: 10px; margin-bottom: 14px; text-align: center; }
        .header-title { font-size: 16pt; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; }
        .header-sub { font-size: 12pt; font-weight: bold; margin-top: 4px; color: #1d4ed8; }
        .clinic-info { font-size: 9pt; color: #555; margin-top: 6px; }
        .clinic-logo-consent { max-width: 120px; height: auto; margin-bottom: 8px; }
        .meta { display: flex; justify-content: space-between; margin-bottom: 14px; font-size: 10pt; }
        .patient-section { border: 1px solid #ccc; padding: 10px 14px; margin-bottom: 14px; border-radius: 3px; }
        .patient-section h3 { font-size: 11pt; margin-bottom: 6px; color: #1d4ed8; }
        .patient-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; font-size: 10pt; }
        .patient-grid .label { color: #555; font-size: 9pt; }
        .section { margin-bottom: 14px; }
        .section h3 { font-size: 11pt; margin-bottom: 6px; color: #1d4ed8; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
        .section p { font-size: 10pt; line-height: 1.5; margin-bottom: 6px; text-align: justify; }
        .risk-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; }
        .risk-table th { background: #1d4ed8; color: white; padding: 5px 8px; text-align: left; font-size: 9pt; }
        .risk-table td { padding: 4px 8px; border-bottom: 1px solid #eee; }
        .risk-table tr:nth-child(even) { background: #f8f9fa; }
        .risk-table .prob-col { width: 100px; text-align: center; font-weight: bold; color: #dc3545; }
        .alternative-list { list-style: none; padding: 0; font-size: 10pt; }
        .alternative-list li { padding: 4px 0; padding-left: 16px; position: relative; }
        .alternative-list li::before { content: "▸"; position: absolute; left: 0; color: #1d4ed8; }
        .disclaimer { border: 1px solid #333; padding: 12px 14px; margin: 20px 0; font-size: 9.5pt; line-height: 1.5; background: #fffdf5; font-style: italic; }
        .signatures { display: flex; justify-content: space-between; margin-top: 40px; page-break-inside: avoid; }
        .sig-block { text-align: center; }
        .sig-line { border-top: 1px solid #111; width: 170px; margin: 0 auto 4px; }
        .sig-name { font-size: 10pt; font-weight: bold; }
        .sig-label { font-size: 9pt; color: #555; }
        @media print {
          @page { margin: 8mm 10mm; size: A4; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
          .risk-table th { background: #1d4ed8 !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="no-print" style={{ background: "#f0f0f0", padding: "10px 20px", display: "flex", gap: 12, alignItems: "center" }}>
        <button
          onClick={() => window.print()}
          style={{ padding: "6px 16px", background: "#1d4ed8", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
        >
          Imprimir / Guardar PDF
        </button>
        <button
          onClick={() => window.close()}
          style={{ padding: "6px 16px", background: "#6b7280", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}
        >
          Cerrar
        </button>
      </div>

      <div className="page">
        <div className="header">
          {logoUrl && (
            <img src={logoUrl} alt="Logo" className="clinic-logo-consent" />
          )}
          <div className="header-title">Consentimiento Informado</div>
          <div className="header-sub">{consent?.template_name}</div>
          {(clinicName && clinicName !== "eCliniq") && (
            <div className="clinic-info">
              {clinicName}{clinicAddress ? ` — ${clinicAddress}` : ""}
            </div>
          )}
        </div>

        <div className="meta">
          <span>{clinicAddress || "Santo Domingo"}, {fmt(proc.procedure_date)}</span>
        </div>

        <div className="patient-section">
          <h3>Información del Paciente</h3>
          <div className="patient-grid">
            <div>
              <div className="label">Nombre Completo</div>
              <div><strong>{patient?.first_name} {patient?.last_name}</strong></div>
            </div>
            <div>
              <div className="label">Identificación</div>
              <div>{patient?.identification || "—"}</div>
            </div>
            <div>
              <div className="label">Edad</div>
              <div>{age !== null ? `${age} años` : "—"}</div>
            </div>
            <div>
              <div className="label">Cirujano Responsable</div>
              <div>Dr. {doctor?.first_name} {doctor?.last_name}</div>
            </div>
            <div>
              <div className="label">Procedimiento</div>
              <div>{proc.procedure_name}</div>
            </div>
            <div>
              <div className="label">Exequátur</div>
              <div>{(doctor as any)?.license_number || "—"}</div>
            </div>
          </div>
        </div>

        <div className="section">
          <h3>Descripción del Procedimiento</h3>
          <p>{consent?.procedure_description || "El cirujano tratante le ha explicado el procedimiento de forma individualizada."}</p>
        </div>

        <div className="section">
          <h3>Riesgos Generales</h3>
          <p>{consent?.general_risks || "Toda cirugía conlleva riesgos de infección, hemorragia y reacción a la anestesia."}</p>
        </div>

        {risks.length > 0 && (
          <div className="section">
            <h3>Riesgos Específicos del Procedimiento</h3>
            <table className="risk-table">
              <thead>
                <tr>
                  <th>Riesgo</th>
                  <th className="prob-col">Probabilidad</th>
                </tr>
              </thead>
              <tbody>
                {risks.map((r, i) => (
                  <tr key={i}>
                    <td>{r.description}</td>
                    <td className="prob-col">{r.probability || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {alternatives.length > 0 && (
          <div className="section">
            <h3>Alternativas al Procedimiento</h3>
            <ul className="alternative-list">
              {alternatives.map((a, i) => (
                <li key={i}>{a.description}</li>
              ))}
            </ul>
          </div>
        )}

        {notes.length > 0 && (
          <div className="section">
            <h3>Notas Adicionales</h3>
            {notes.map((n, i) => (
              <p key={i}>{n.description}</p>
            ))}
          </div>
        )}

        <div className="disclaimer">
          <strong>Declaración del Paciente:</strong> He recibido información clara y completa sobre el procedimiento quirúrgico al que seré sometido/a, incluyendo su naturaleza, beneficios esperados, riesgos potenciales y alternativas disponibles. He tenido la oportunidad de hacer preguntas y todas han sido respondidas satisfactoriamente. Comprendo que la medicina y la cirugía no son ciencias exactas y que no se me han garantizado resultados específicos. Autorizo al Dr. {doctor?.first_name} {doctor?.last_name} y al equipo médico a realizar el procedimiento descrito, así como cualquier procedimiento adicional que se considere necesario durante la cirugía. He comprendido la información proporcionada y otorgo mi consentimiento de forma libre y voluntaria.
        </div>

        <div className="signatures">
          <div className="sig-block">
            <div className="sig-line"></div>
            <div className="sig-name">{patient?.first_name} {patient?.last_name}</div>
            <div className="sig-label">Paciente</div>
            <div className="sig-label" style={{ marginTop: 2 }}>{patient?.identification ? `Cédula: ${patient.identification}` : ""}</div>
          </div>
          <div className="sig-block">
            <div className="sig-line"></div>
            <div className="sig-name">Dr. {doctor?.first_name} {doctor?.last_name}</div>
            <div className="sig-label">Cirujano Responsable</div>
            <div className="sig-label" style={{ marginTop: 2 }}>{(doctor as any)?.license_number ? `Exequátur: ${(doctor as any).license_number}` : ""}</div>
          </div>
          <div className="sig-block">
            <div className="sig-line"></div>
            <div className="sig-name"></div>
            <div className="sig-label">Testigo</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InformedConsentPrint;
